import { Box } from '@chakra-ui/react';
import { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import 'react-grid-layout/css/styles.css';
import { Layout, Layouts, Responsive, WidthProvider } from 'react-grid-layout';

import './pagegrid.css';
import ExportInstruments from './Instruments';
import {
  EXPORT_GRID_PADDING,
  EXPORT_PREVIEW_BOTTOM_PADDING,
  EXPORT_ROW_HEIGHT,
  getLayoutGridHeight,
  measureGridContentHeight,
} from './exportGridHeight';

const breakpoints = { lg: 480, xxs: 0 };
const cols = { lg: 12, xxs: 1 };
const emptyLayout: Layout[] = [];

export interface PageGridProperties {
  page: any;
  allInstruments: any[];
  allInstrumentsData?: any[];
}

const PageGrid = ({ page, allInstruments, allInstrumentsData }: PageGridProperties) => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<keyof typeof cols>('lg');
  const [savedLayout, setSavedLayout] = useState<Layouts>({});
  const [gridContentHeight, setGridContentHeight] = useState(300);
  const gridWrapperReference = useRef<HTMLDivElement>(null);
  const gridContentHeightReference = useRef(300);

  const activeLayout = savedLayout[currentBreakpoint] ?? emptyLayout;

  const getNextAvailablePosition = (
    layout: Layout[],
    columns: number,
    defaultSize = { w: 2, h: 3 },
  ) => {
    for (let y = 0; y < 20; y += 1) {
      for (let x = 0; x <= columns - defaultSize.w; x += 1) {
        const isOccupied = layout.some(
          (item) =>
            x < item.x + item.w &&
            x + defaultSize.w > item.x &&
            y < item.y + item.h &&
            y + defaultSize.h > item.y,
        );
        if (!isOccupied) {
          return { x, y, ...defaultSize };
        }
      }
    }
    return { x: 0, y: 0, ...defaultSize };
  };

  useEffect(() => {
    if (page?.instrument_layout) {
      setSavedLayout(page.instrument_layout);
    }
  }, [page]);

  useEffect(() => {
    setSavedLayout((previousLayout: any) => {
      const updatedLayouts = { ...previousLayout };

      Object.keys(cols).forEach((breakpoint) => {
        const existingLayout = updatedLayouts[breakpoint] || [];
        const newLayout = [...existingLayout];

        allInstruments.forEach((item) => {
          if (!existingLayout.some((layoutItem: any) => layoutItem.i === String(item.id))) {
            const position = getNextAvailablePosition(
              newLayout,
              cols[breakpoint as keyof typeof cols],
              {
                h: item.instrument_type === 'kpi' ? 2 : 3,
                w: item.instrument_type === 'table' ? 3 : 2,
              },
            );
            newLayout.push({
              i: String(item.id),
              ...position,
              minH: item.instrument_type === 'kpi' ? 2 : 3,
              minW: item.instrument_type === 'table' ? 3 : 2,
            });
          }
        });

        updatedLayouts[breakpoint] = newLayout;
      });

      return updatedLayouts;
    });
  }, [allInstruments]);

  useEffect(() => {
    const initialHeight = getLayoutGridHeight(activeLayout);
    gridContentHeightReference.current = initialHeight;
    setGridContentHeight(initialHeight);
  }, [activeLayout]);

  const ResponsiveReactGridLayout = useMemo(() => WidthProvider(Responsive), []);

  const applyGridHeight = useCallback((neededHeight: number) => {
    const wrapper = gridWrapperReference.current;
    if (!wrapper) return;

    const gridElement = wrapper.querySelector('.react-grid-layout') as HTMLElement | null;
    if (!gridElement) return;

    gridElement.style.minHeight = `${neededHeight}px`;
    gridElement.style.height = `${neededHeight}px`;

    if (Math.abs(neededHeight - gridContentHeightReference.current) > 1) {
      gridContentHeightReference.current = neededHeight;
      setGridContentHeight(neededHeight);
    }
  }, []);

  const measureGridHeight = useCallback(() => {
    const wrapper = gridWrapperReference.current;
    if (!wrapper) return;

    const neededHeight = measureGridContentHeight(
      wrapper,
      activeLayout,
      EXPORT_PREVIEW_BOTTOM_PADDING,
    );
    applyGridHeight(neededHeight);
  }, [activeLayout, applyGridHeight]);

  useLayoutEffect(() => {
    if (!allInstrumentsData || activeLayout.length === 0) {
      return () => {};
    }

    const runMeasure = () => {
      requestAnimationFrame(() => {
        measureGridHeight();
      });
    };

    runMeasure();

    const timeoutIds = [50, 150, 400, 800, 1500, 3000].map((delay) =>
      window.setTimeout(runMeasure, delay),
    );

    const observer = new ResizeObserver(runMeasure);
    const wrapper = gridWrapperReference.current;
    if (wrapper) {
      observer.observe(wrapper);
      wrapper.querySelectorAll('.react-grid-item, .dashboard-item').forEach((item) => {
        observer.observe(item);
      });
    }

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      observer.disconnect();
    };
  }, [activeLayout, allInstrumentsData, allInstruments, measureGridHeight]);

  return (
    <Box
      ref={gridWrapperReference}
      className={`page-grid-container page-${page.id}`}
      marginLeft={-5}
      marginRight={-5}
      position='relative'
      overflow='visible'
      height={`${gridContentHeight}px`}
      minH={`${gridContentHeight}px`}
    >
      <ResponsiveReactGridLayout
        resizeHandles={[]}
        useCSSTransforms
        style={{
          borderRadius: '8px',
          padding: `${EXPORT_GRID_PADDING}px`,
          minHeight: gridContentHeight,
          height: gridContentHeight,
        }}
        className={`dashboard-grid-container page-grid-${page.id}`}
        allowOverlap={false}
        layouts={savedLayout}
        preventCollision={false}
        rowHeight={EXPORT_ROW_HEIGHT}
        autoSize={false}
        isDraggable={false}
        isResizable={false}
        onLayoutChange={measureGridHeight}
        onBreakpointChange={(newBreakpoint) =>
          setCurrentBreakpoint(newBreakpoint as keyof typeof cols)
        }
        margin={[12, 12]}
        breakpoints={breakpoints}
        cols={cols}
      >
        {activeLayout.map((layoutItem) => {
          if (!allInstruments) return null;
          if (!allInstrumentsData) return null;

          const item = allInstruments?.find((data: any) => String(data.id) === layoutItem.i);
          if (!item) return null;

          const instrumentData = allInstrumentsData[item.id];

          return (
            <Box
              className='dashboard-item grid-item'
              key={`${page.id}-${item.id}`}
              data-grid={{ ...layoutItem, isResizable: false, isDraggable: false }}
              bg='white'
              border='1px solid #e0e0e0'
              borderColor='gray.200'
              borderRadius='md'
              p={4}
            >
              {instrumentData && (
                <ExportInstruments widget={item} output={instrumentData || null} />
              )}
              {!instrumentData && (
                <Box opacity={0.7}>
                  No data available for {item.instrument_type}, {item.id}
                </Box>
              )}
            </Box>
          );
        })}
      </ResponsiveReactGridLayout>
    </Box>
  );
};

export default PageGrid;
